import {AxiosResponse, CancelToken} from 'axios';
import {ActivityInfo} from '../backend/interfaces/activity-info';
import {BackendApiManager} from '../managers/backend-api';
import {Logger} from '../utils/managers/logger';

export class ActivitiesRepository {
  // Singleton Instance
  private static _instance: ActivitiesRepository;

  // Private
  private logger: Logger;

  static get instance(): ActivitiesRepository {
    if (!this._instance) {
      this._instance = new ActivitiesRepository();
    }
    return this._instance;
  }

  constructor() {
    // Create Logger
    this.logger = new Logger('ActivitiesRepository');
  }

  // Get all activities of a user as an informal caregiver
  public async getAsInformalCaregiver(
    userId: string,
    localized: boolean,
    limit: number,
    skip: number,
    cancelToken?: CancelToken
  ): Promise<{totalCount: number; data: Array<ActivityInfo>}> {
    // Set url
    let url: string = `/users/${userId}/activities?limit=${limit}&skip=${skip}&localize=${localized.toString()}`;

    // Get activities from backend
    const result: AxiosResponse<Array<ActivityInfo>> = await BackendApiManager.instance.get<
      Array<ActivityInfo>
    >(url, {
      cancelToken,
    });

    // Return
    return {data: result.data, totalCount: parseInt(result.headers['x-total-count'] || '', 10)};
  }

  // Get all activities of an entity or a user in an entity as a professional caregiver
  public async getAsAffiliatedForEntity(
    entityId: string,
    localized: boolean,
    limit: number,
    skip: number,
    userId?: string,
    participantUserId?: string,
    cancelToken?: CancelToken
  ): Promise<{totalCount: number; data: Array<ActivityInfo>}> {
    // Set url
    let url: string = `/entities/${entityId}/activities?limit=${limit}&skip=${skip}&localize=${localized.toString()}`;

    // Filter by user
    if (userId) {
      url += `&userId=${userId}`;
    }

    // Filter by participant user
    if (participantUserId) {
      url += `&participantUserId=${participantUserId}`;
    }

    // Get activities from backend
    const result: AxiosResponse<Array<ActivityInfo>> = await BackendApiManager.instance.get<
      Array<ActivityInfo>
    >(url, {
      cancelToken,
    });

    // Return
    return {data: result.data, totalCount: parseInt(result.headers['x-total-count'] || '', 10)};
  }

  // Get all activities of the system, an entity or a user in an entity as a system administrator
  public async getAsSystemAdministrator(
    localized: boolean,
    limit: number,
    skip: number,
    entityId?: string,
    userId?: string,
    participantUserId?: string,
    cancelToken?: CancelToken
  ): Promise<{totalCount: number; data: Array<ActivityInfo>}> {
    // Set url
    let url: string = `/activities?limit=${limit}&skip=${skip}&localize=${localized.toString()}`;

    // Filter by entity
    if (entityId) {
      url += `&entityId=${entityId}`;
    }

    // Filter by user
    if (userId) {
      url += `&userId=${userId}`;
    }

    // Filter by participant user
    if (participantUserId) {
      url += `&participantUserId=${participantUserId}`;
    }

    // Get activities from backend
    const result: AxiosResponse<Array<ActivityInfo>> = await BackendApiManager.instance.get<
      Array<ActivityInfo>
    >(url, {
      cancelToken,
    });

    // Return
    return {data: result.data, totalCount: parseInt(result.headers['x-total-count'] || '', 10)};
  }
}
